import React from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import { sanitizeHtml } from "../markdown";

// Channel treatments — email, LinkedIn and call steps each get their own
// visual identity, mirroring the sequence builder.
const CHANNEL_META = {
  email: { icon: "mail", label: "Email", accent: "email" },
  linkedin: { icon: "person_add", label: "LinkedIn", accent: "linkedin" },
  call: { icon: "call", label: "Call", accent: "call" }
};

const LINKEDIN_ACTIONS = {
  view_profile: "View profile",
  connection_request: "Connection request",
  message: "Message",
  inmail: "InMail"
};

const dayOffsets = steps => {
  let day = 1;
  return steps.map((step, index) => {
    if (index > 0) day += Number(step.days_later) || 0;
    return day;
  });
};

/**
 * Pre-create sequence draft as builder-style step cards ("Step 1 · Day 1"),
 * per-channel styling, and a strong Create CTA. The user always sees WHAT
 * will be created before anything is written, and knows it stays editable.
 */
const DraftReviewBlock = observer(({ block }) => {
  const steps = block.steps || [];
  const days = dayOffsets(steps);
  const consumed = block.status === "created" || block.clickedActionId;

  const createNow = () => {
    copilotStore.clickCta(block.id, {
      actionId: "create_drafted_sequence",
      label: "Create this sequence"
    });
  };

  return (
    <div className="copilotDraft">
      <div className="copilotDraft__head">
        <i className="material-icons-outlined">view_list</i>
        <span className="copilotDraft__name">{block.name}</span>
        <span className="copilotDraft__meta">
          {steps.length} steps · {days[days.length - 1] || 1} days
        </span>
      </div>
      <div className="copilotDraft__steps">
        {steps.map((step, index) => {
          const meta = CHANNEL_META[step.type] || CHANNEL_META.email;
          return (
            <div
              key={index}
              className={`copilotDraft__step copilotDraft__step--${meta.accent}`}
            >
              <div className="copilotDraft__rail">
                <span className="copilotDraft__stepNum">Step {index + 1}</span>
                <span className="copilotDraft__stepDay">Day {days[index]}</span>
              </div>
              <div className="copilotDraft__card">
                <div className="copilotDraft__cardHead">
                  <i className="material-icons-outlined">{meta.icon}</i>
                  <span>{meta.label}</span>
                  {step.type === "linkedin" && (
                    <span className="copilotDraft__liAction">
                      {LINKEDIN_ACTIONS[step.linkedin_action] || "Task"}
                      {step.automated === false ? " · manual" : ""}
                    </span>
                  )}
                </div>
                {step.type === "email" && step.subject && (
                  <div className="copilotDraft__subject">{step.subject}</div>
                )}
                {(step.body || step.message || step.notes) && (
                  <div
                    className="copilotDraft__body"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(
                        (step.body || step.message || step.notes || "").replace(
                          /\n/g,
                          "<br/>"
                        )
                      )
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="copilotDraft__footer">
        <button
          type="button"
          className="copilotDraft__createBtn"
          disabled={Boolean(consumed) || copilotStore.streaming}
          onClick={createNow}
        >
          {/* Classic-set glyph names only — the pinned Material Icons font
              predates "bolt", which renders as an empty box that still takes
              up width. "flash_on" is the same icon in the old set. */}
          <i className="material-icons-outlined">
            {consumed ? "check_circle" : "flash_on"}
          </i>
          {consumed ? "Creating…" : "Create this sequence"}
        </button>
        <span className="copilotDraft__note">
          You can fine-tune every step manually in Sequences after it's
          created.
        </span>
      </div>
    </div>
  );
});

export default DraftReviewBlock;
